import React, { useEffect, useState } from "react";
import PricingCard from "./PricingCard";
import PlanTabs from "./PlanTabs";
import SectionBadge from "@/components/shared/Title";
import { IoPricetagOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";

import MotionDiv from "@/components/animations/MotionDiv";
import { fadeUp, stagger, scaleUp } from "@/animations/variants";
import { AnimatePresence, motion } from "framer-motion";

import { usePackages } from "@/hooks/usePackages";

const PricingSection = () => {
  const { t, i18n } = useTranslation();
  const isAR = i18n.language?.startsWith("ar");

  const [activePlan, setActivePlan] = useState(null);

  const { groupedPlans, employeeCounts, loading, error } = usePackages(isAR);

  useEffect(() => {
    if (!activePlan && employeeCounts.length > 0) {
      setActivePlan(employeeCounts[0]);
    }
  }, [employeeCounts, activePlan]);

  const plan = activePlan ? groupedPlans[activePlan] : null;

  return (
    <section
      id="pricing"
      dir={isAR ? "rtl" : "ltr"}
      className="bg-[#f3f6f9] py-14"
    >
      <div className="container mx-auto px-4 text-center">
        <MotionDiv variants={fadeUp} className="mb-8 text-center">
          <SectionBadge
            text={t("pricing.badge")}
            icon={<IoPricetagOutline />}
          />
        </MotionDiv>

        <MotionDiv variants={fadeUp}>
          <h2 className="text-primary text-center text-3xl font-bold md:text-4xl">
            {t("pricing.title")}
          </h2>
        </MotionDiv>

        <MotionDiv variants={fadeUp}>
          <p className="mx-auto mt-4 max-w-2xl text-center text-base font-medium text-gray-500 md:text-xl">
            {t("pricing.subtitle")}
          </p>
        </MotionDiv>

        {loading ? (
          <div className="mt-12 text-center text-gray-500">Loading...</div>
        ) : error ? (
          <div className="mt-12 text-center text-red-500">{error}</div>
        ) : !plan ? (
          <div className="mt-12 text-center text-gray-500">
            No packages available
          </div>
        ) : (
          <>
            <MotionDiv
              variants={fadeUp}
              className="mx-auto mt-8 flex w-fit justify-center"
            >
              <PlanTabs
                active={activePlan}
                setActive={setActivePlan}
                plans={employeeCounts}
              />
            </MotionDiv>

            <MotionDiv
              variants={stagger}
              className="mx-auto mt-12 grid max-w-6xl gap-8 md:grid-cols-3"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={activePlan}
                  initial={{ opacity: 0, y: 20, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.96 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="contents"
                >
                  {/* Monthly */}
                  <MotionDiv variants={scaleUp}>
                    <PricingCard plan={plan} type="monthly" />
                  </MotionDiv>

                  {/* Yearly (Featured - Center) */}
                  <MotionDiv variants={scaleUp}>
                    <PricingCard plan={plan} type="yearly" highlight />
                  </MotionDiv>

                  {/* Half Year */}
                  <MotionDiv variants={scaleUp}>
                    <PricingCard plan={plan} type="halfYear" />
                  </MotionDiv>
                </motion.div>
              </AnimatePresence>
            </MotionDiv>
          </>
        )}
      </div>
    </section>
  );
};

export default PricingSection;
